/**
 * Sticker atlas loader (plan 120, PR1).
 *
 * Loads `/sprites/sticker-icons{,@2x}.{png,json}` once per session and exposes:
 *   - `atlasUrl`: PNG path for Deck.gl IconLayer `getIcon.url` (or CSS bg).
 *   - `mapping`: Deck.gl `iconMapping` (slug → {x,y,width,height,anchorY,mask}).
 *   - `meta`: raw sprite JSON (slug → {x,y,width,height,pixelRatio}); the
 *     library picker uses this to crop thumbnails via CSS background-position.
 *   - `getIconBox(slug)`: convenience read of `meta[slug]`.
 *   - `pixelRatio`: 1 or 2 — signals which atlas variant we loaded.
 *
 * Lands in PR1 even though no consumer until PR3 (admin library/render) and
 * PR5 (visitor render). Shared infrastructure — do not duplicate.
 */

import { useEffect, useMemo, useState } from "react";
import {
  convertSpriteToDeckIconMappingCentered,
  type DeckIconMapping,
  type SpriteMetadata,
} from "@wondermaps/maps";

export interface StickerAtlas {
  atlasUrl: string;
  pixelRatio: 1 | 2;
  /** Natural pixel dimensions of the loaded atlas PNG. */
  atlasWidth: number;
  atlasHeight: number;
  meta: Record<string, SpriteMetadata>;
  mapping: Record<string, DeckIconMapping>;
  getIconBox: (slug: string) => SpriteMetadata | null;
}

interface State {
  atlasUrl: string;
  pixelRatio: 1 | 2;
  atlasWidth: number;
  atlasHeight: number;
  meta: Record<string, SpriteMetadata>;
  mapping: Record<string, DeckIconMapping>;
}

export function useStickerAtlas(): StickerAtlas | null {
  const [state, setState] = useState<State | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const useRetina =
        typeof window !== "undefined" && window.devicePixelRatio > 1;
      const suffix = useRetina ? "@2x" : "";
      const atlasUrl = `/sprites/sticker-icons${suffix}.png`;
      const jsonUrl = `/sprites/sticker-icons${suffix}.json`;

      try {
        const [meta, dims] = await Promise.all([
          fetch(jsonUrl).then((r) => {
            if (!r.ok) throw new Error(`sticker atlas json ${r.status}`);
            return r.json() as Promise<Record<string, SpriteMetadata>>;
          }),
          new Promise<{ width: number; height: number }>((resolve, reject) => {
            const img = new Image();
            img.onload = () =>
              resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () =>
              reject(new Error(`sticker atlas png failed to load`));
            img.src = atlasUrl;
          }),
        ]);
        if (cancelled) return;
        setState({
          atlasUrl,
          pixelRatio: useRetina ? 2 : 1,
          atlasWidth: dims.width,
          atlasHeight: dims.height,
          meta,
          // Stickers use center-anchor (plan 120 PR9 hotfix) — they're
          // billboard:false flat-on-ground, so bottom-anchor offsets the
          // visual center from the cursor / committed coordinate.
          mapping: convertSpriteToDeckIconMappingCentered(meta),
        });
      } catch (err) {
        if (!cancelled) {
          console.warn("[useStickerAtlas] failed to load atlas:", err);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo<StickerAtlas | null>(() => {
    if (!state) return null;
    return {
      ...state,
      getIconBox: (slug: string) => state.meta[slug] ?? null,
    };
  }, [state]);
}
